/**
 * BL-2020 — the one rule for "this amount can be charged in its currency": no
 * residue below the currency's minor unit (¥1, $0.01). Shared by the bid engine's
 * increment check and the admin post-sale doors' request boundary (ADR 060 —
 * Money Type), so a fractional yen is refused the same way everywhere.
 */
import Decimal from 'decimal.js';

import { minorUnits, type SupportedCurrency } from './currency';
import { Money } from './money';

/**
 * Whether an amount is a whole number of its currency's minor unit. Reads the
 * VALUE, not the stored scale: a `numeric(18,4)` read of ¥5,000 ("5000.0000") is
 * whole yen. The sign is the caller's rule (a bid must also be positive).
 *
 * @param money - The amount.
 * @returns True for ¥1,100 or $1,300.25; false for ¥1,100.5 or $1,300.505.
 */
export function isWholeMinorUnits(money: Money): boolean {
    return money.toDecimal().decimalPlaces() <= minorUnits(money.currency);
}

/**
 * The smallest chargeable amount in a currency — ¥1, $0.01 — for refusal copy
 * that names the step through `format()`.
 *
 * @param currency - A supported ISO 4217 code.
 * @returns One minor unit of the currency.
 */
export function minorUnitStep(currency: SupportedCurrency): Money {
    return Money.of(new Decimal(10).pow(-minorUnits(currency)), currency);
}
